/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MUITextField, {
  TextFieldProps as MUITextFieldProps,
  TextFieldProps,
} from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DeleteIcon from '@mui/icons-material/Delete';
import Button from '@mui/material/Button';
import { useController } from 'react-hook-form';
import { FieldProps } from '../FieldProps';
import FieldContainer from '../FieldContainer';
import useFormLabel from '../useFormLabel';
import ValidationError from '../ValidationError';
import BorderWrapper from './BorderWrapper';
import UploadDialog from './UploadDialog';
import useFrontendResourcesApi from '../../../../frontendResources/useFrontendResourcesApi';
import useDownload from './useDownload';

export interface FileFieldProps extends FieldProps<MUITextFieldProps> {
  optional?: boolean;
  // the return value of onUpload is entered into the TextField
  onUpload?: (file: File) => Promise<void | string> | void | string;
  label?: string;
  allowManualEdit?: boolean;
  textFieldProps?: TextFieldProps;
}

export default function FileField({
  control,
  name,
  rules,
  type,
  optional = false,
  onUpload,
  label,
  allowManualEdit = false,
  textFieldProps,
}: FileFieldProps) {
  const { t } = useTranslation();
  const resourceApi = useFrontendResourcesApi();
  const { download } = useDownload();

  const textFieldLabel = useFormLabel(name, label, {
    removeIndex: true,
    optionalHint: optional,
  });

  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({ name, control, rules });

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  const handleUpload = useCallback(
    async (file: File) => {
      let newValue;
      if (onUpload) {
        newValue = await onUpload?.(file);
      } else {
        newValue = await resourceApi.uploadFile({ file });
      }
      if (newValue) onChange(newValue);
      setUploadDialogOpen(false);
    },
    [onChange, onUpload, resourceApi],
  );

  const handleDelete = useCallback(() => {
    onChange('');
  }, [onChange]);

  return (
    <>
      <UploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onUpload={handleUpload}
      />

      <FieldContainer>
        <BorderWrapper direction="row" gap="4px">
          <Stack sx={{ flexGrow: 1 }} alignItems="flex-end" gap={0.5}>
            <MUITextField
              type={type}
              label={textFieldLabel}
              variant="outlined"
              value={value || ''}
              onChange={onChange}
              error={!!error}
              helperText={error && <ValidationError error={error} />}
              fullWidth
              disabled={!allowManualEdit}
              {...textFieldProps}
            />
            <Stack direction="row" gap={1}>
              <Button
                variant="outlined"
                color="error"
                disabled={!value}
                startIcon={<DeleteIcon />}
                sx={{ flexGrow: 0 }}
                onClick={handleDelete}
              >
                {t('form.fileField.removeFileButton')}
              </Button>
              <Button
                variant="outlined"
                disabled={!value}
                startIcon={<CloudDownloadIcon />}
                sx={{ flexGrow: 0 }}
                onClick={() => {
                  download(value);
                }}
              >
                {t('form.fileField.downloadButton')}
              </Button>
              <Button
                variant="contained"
                startIcon={<CloudUploadIcon />}
                sx={{ flexGrow: 0 }}
                onClick={() => setUploadDialogOpen(true)}
              >
                {t('form.fileField.uploadButton')}
              </Button>
            </Stack>
          </Stack>
        </BorderWrapper>
      </FieldContainer>
    </>
  );
}
